import {
  followUpLanesFor,
  type CreateCoachFollowUpRequest,
  type FollowUpOwner,
  type FollowUpOwnerRole,
  type WorkQueueLane,
} from "@care-by-ella/contracts";

// TES-1400 (Ö-426, Emelie 2026-10-15) — "Följ upp senare" on a work-queue
// card:
//   "Funktionen kräver alltid: ansvarig person eller roll, datum och tid för återkomst, vilken arbetskö ärendet återkommer i, synlig orsak, auditlogg. Ett ärende får inte försvinna till en ospecificerad senarelista."
// The card used to ask for a date alone (saved as 09:00), made the setter
// the owner, sent it back to her own "Uppföljningar" and used the card's
// title as the prompt. Pure, so the admin's node tests run it as is.

/** "me" is the signed-in person; a role is every coach or every doctor. */
export type FollowUpOwnerChoice = "me" | FollowUpOwnerRole;

export interface FollowUpLaterValue {
  owner: FollowUpOwnerChoice;
  /** yyyy-mm-dd */
  date: string;
  /** hh:mm, Swedish local time. */
  time: string;
  lane: WorkQueueLane | "";
  reason: string;
}

export const EMPTY_FOLLOW_UP_LATER: FollowUpLaterValue = {
  owner: "me",
  date: "",
  time: "09:00",
  lane: "",
  reason: "",
};

/** The role whose lanes the follow-up may come back in. */
export function followUpOwnerRole(
  owner: FollowUpOwnerChoice,
  myRole: FollowUpOwnerRole,
): FollowUpOwnerRole {
  return owner === "me" ? myRole : owner;
}

/** The lanes offered for this owner. */
export function followUpLaneOptions(
  owner: FollowUpOwnerChoice,
  myRole: FollowUpOwnerRole,
): readonly WorkQueueLane[] {
  return followUpLanesFor(followUpOwnerRole(owner, myRole));
}

/** Why it cannot be saved yet, in the clinician's words; null when it can. */
export function followUpLaterProblem(
  v: FollowUpLaterValue,
  myRole: FollowUpOwnerRole,
  now: Date = new Date(),
): string | null {
  if (!v.date || !/^\d{2}:\d{2}$/.test(v.time))
    return "Välj datum och tid för återkomst.";
  if (!v.lane || !followUpLaneOptions(v.owner, myRole).includes(v.lane))
    return "Välj vilken arbetskö ärendet återkommer i.";
  if (!v.reason.trim()) return "Skriv en synlig orsak.";
  if (new Date(`${v.date}T${v.time}:00`).getTime() <= now.getTime())
    return "Återkomsten måste ligga framåt i tiden.";
  return null;
}

/** The request the card sends, once followUpLaterProblem is null. */
export function followUpLaterPayload(
  v: FollowUpLaterValue,
  me: string,
  source: { itemId: string },
): CreateCoachFollowUpRequest {
  const owner: FollowUpOwner =
    v.owner === "me"
      ? { kind: "person", employeeId: me }
      : { kind: "role", role: v.owner };
  return {
    // The browser's own clock is Swedish for every clinician; the server
    // stores the instant.
    dueAt: new Date(`${v.date}T${v.time}:00`).toISOString(),
    prompt: v.reason.trim(),
    sourceType: "coach_note",
    sourceId: source.itemId,
    owner,
    returnLane: v.lane as WorkQueueLane,
  };
}
